import { Search, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import type { User } from "../api";
import { Avatar } from "./ui";

type UserChoice = Pick<User, "id" | "displayName"> & Partial<Pick<User, "email" | "jobTitle" | "avatarVersion">>;

/** Callers supply their permission-filtered candidates; this component only selects. */
export function UserPicker({ users, value, onChange, multiple = false, label, searchLabel = `${label} 검색`,
  disabled = false, required = false, invalid = false, name, emptyText, suggestedIds = [], searchText,
}: {
  users: UserChoice[]; value: string[]; onChange: (ids: string[]) => void; multiple?: boolean;
  label: string; searchLabel?: string; disabled?: boolean; required?: boolean; invalid?: boolean;
  name?: string; emptyText?: string; suggestedIds?: string[]; searchText?: (user: UserChoice) => string;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { input.current?.setCustomValidity(required && !value.length ? "검색 결과에서 구성원을 선택해 주세요." : ""); }, [required, value.length]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const normalized = query.trim().toLocaleLowerCase();
  const results = users.filter(user => !value.includes(user.id) && (normalized
    ? `${user.displayName} ${user.email || ""} ${user.jobTitle || ""} ${searchText?.(user) || ""}`.toLocaleLowerCase().includes(normalized)
    : suggestedIds.includes(user.id))).slice(0, 50);
  const visible = open && (Boolean(normalized) || suggestedIds.length > 0);
  function pick(userId: string) {
    onChange(multiple ? [...value, userId] : [userId]);
    setQuery(""); setOpen(false); setActive(-1);
  }
  return <div className="user-picker project-member-picker" onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false); }}>
    <label className="field-label" htmlFor={id}>{label}{required && <em className="required-mark">필수</em>}</label>
    {name && <input type="hidden" name={name} value={value[0] || ""} />}
    {!!value.length && <div className="member-chips">{value.map(userId => {
      const user = users.find(person => person.id === userId);
      return <span key={userId}><Avatar name={user?.displayName || "구성원"} userId={userId} avatarVersion={user?.avatarVersion} size="sm" />
        <b>{user?.displayName || "구성원"}</b><small>{user?.jobTitle || user?.email}</small>
        <button type="button" disabled={disabled} aria-label={`${user?.displayName || "구성원"} 제거`} onClick={() => onChange(value.filter(id => id !== userId))}><X size={14} /></button></span>;
    })}</div>}
    {!value.length && emptyText && <p className="muted">{emptyText}</p>}
    <div className="member-search"><Search size={16} /><input ref={input} id={id} role="combobox" aria-label={searchLabel} aria-expanded={visible}
      aria-controls={`${id}-results`} aria-autocomplete="list" aria-activedescendant={visible && results[active] ? `${id}-${results[active].id}` : undefined}
      aria-invalid={invalid} required={required && !value.length} disabled={disabled} autoComplete="off" value={query}
      placeholder="이름, 이메일 또는 직책으로 검색" onFocus={() => setOpen(true)}
      onChange={event => { setQuery(event.target.value); setOpen(true); setActive(-1); }}
      onKeyDown={event => {
        if (event.nativeEvent.isComposing) return;
        if (event.key === "Escape" && open) { event.preventDefault(); event.stopPropagation(); setOpen(false); }
        if (["ArrowDown", "ArrowUp"].includes(event.key) && results.length) {
          event.preventDefault(); setOpen(true);
          const next = event.key === "ArrowDown" ? (active + 1) % results.length : active <= 0 ? results.length - 1 : active - 1;
          setActive(next); document.getElementById(`${id}-${results[next].id}`)?.scrollIntoView({ block: "nearest" });
        }
        if (event.key === "Enter") { event.preventDefault(); const picked = results[Math.max(active, 0)]; if (visible && picked) pick(picked.id); }
      }} /></div>
    {visible && <div id={`${id}-results`} className="member-search-results" role="listbox" aria-label={`${label} 검색 결과`}>
      {results.map((user, index) => <button type="button" role="option" id={`${id}-${user.id}`} aria-selected={index === active} tabIndex={-1} key={user.id}
        onMouseDown={event => event.preventDefault()} onClick={() => pick(user.id)}>
        <Avatar name={user.displayName} userId={user.id} avatarVersion={user.avatarVersion} size="sm" />
        <span><b>{user.displayName}</b><small>{user.jobTitle || "구성원"} · {user.email}</small></span><em>{multiple ? "추가" : "선택"}</em>
      </button>)}{!results.length && <p>검색 결과가 없습니다.</p>}
    </div>}
  </div>;
}
